import React, { useState } from "react";
import axios from "axios";
import {
  Building2,
  Mail,
  Phone,
  MapPin,
  Hash,
  ShieldCheck,
  Info,
  Loader2,
  CheckCircle2,
} from "lucide-react";

const Input = ({
  label,
  value,
  onChange,
  type = "text",
  placeholder = "",
  icon: Icon,
}) => {
  return (
    <div>
      <label className="mb-2 block text-xs font-medium text-slate-500">
        {label}
      </label>

      <div className="flex h-12 items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 transition focus-within:border-slate-400 focus-within:ring-4 focus-within:ring-slate-100">
        {Icon ? <Icon size={16} className="shrink-0 text-slate-400" /> : null}

        <input
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent text-sm font-medium text-slate-800 outline-none placeholder:text-slate-400"
        />
      </div>
    </div>
  );
};

const Tooltip = ({ text }) => {
  return (
    <div className="group relative inline-flex">
      <button
        type="button"
        className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-slate-500"
      >
        <Info size={12} />
      </button>

      <div className="pointer-events-none absolute left-1/2 top-7 z-20 hidden w-64 -translate-x-1/2 rounded-xl border border-slate-200 bg-white p-3 text-xs leading-5 text-slate-600 shadow-xl group-hover:block">
        {text}
      </div>
    </div>
  );
};

const RegisterPharmacy = () => {
  const [form, setForm] = useState({
    name: "",
    licenseNumber: "",
    cacNumber: "",
    email: "",
    phoneNumber: "",
    whatsappNumber: "",
    address: "",
    lat: "",
    lng: "",
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (key, value) => {
    setSuccess(false);
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleNumberChange = (key, value) => {
    setSuccess(false);

    if (/^-?\d*\.?\d*$/.test(value)) {
      setForm((prev) => ({ ...prev, [key]: value }));
    }
  };

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.phoneNumber || !form.address) {
      alert("Please fill pharmacy name, email, phone number, and address.");
      return;
    }

    try {
      setLoading(true);
      setSuccess(false);

      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/pharmacy/register`, {
        ...form,
        location: {
          address: form.address,
          lat: form.lat ? Number(form.lat) : null,
          lng: form.lng ? Number(form.lng) : null,
        },
      });

      setSuccess(true);

      setForm({
        name: "",
        licenseNumber: "",
        cacNumber: "",
        email: "",
        phoneNumber: "",
        whatsappNumber: "",
        address: "",
        lat: "",
        lng: "",
      });
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-10 sm:px-6">
      <div className="mx-auto w-full max-w-3xl">
        {/* Brand */}
        <div className="mb-8 flex items-center justify-center gap-3">
          <img
            src="/zavira_nobg.png"
            alt="Zaviramed"
            className="h-10 w-10 object-contain"
          />

          <div>
            <h1 className="text-base font-semibold text-slate-950">
              Zaviramed
            </h1>
            <p className="text-xs text-slate-400">Pharmacy registration</p>
          </div>
        </div>

        {/* Card */}
        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-8">
          <div className="mb-8">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
              <Building2 size={22} />
            </div>

            <h2 className="text-2xl font-semibold tracking-[-0.04em] text-slate-950">
              Register pharmacy
            </h2>

            <p className="mt-2 max-w-xl text-sm leading-6 text-slate-500">
              Add your pharmacy details for verification and prescription
              costing.
            </p>
          </div>

          {success && (
            <div className="mb-6 flex items-start gap-3 rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-sm text-emerald-700">
              <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
              <p className="font-medium">
                Pharmacy registered successfully. Your account will be reviewed.
              </p>
            </div>
          )}

          <div className="space-y-6">
            {/* Pharmacy */}
            <div>
              <p className="mb-4 text-xs font-medium uppercase tracking-wide text-slate-400">
                Pharmacy details
              </p>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Input
                    label="Pharmacy name"
                    value={form.name}
                    placeholder="Enter pharmacy name"
                    icon={Building2}
                    onChange={(value) => handleChange("name", value)}
                  />
                </div>

                <Input
                  label="License number"
                  value={form.licenseNumber}
                  placeholder="License number"
                  icon={ShieldCheck}
                  onChange={(value) => handleChange("licenseNumber", value)}
                />

                <Input
                  label="CAC number"
                  value={form.cacNumber}
                  placeholder="CAC number"
                  icon={Hash}
                  onChange={(value) => handleChange("cacNumber", value)}
                />
              </div>
            </div>

            {/* Contact */}
            <div>
              <p className="mb-4 text-xs font-medium uppercase tracking-wide text-slate-400">
                Contact details
              </p>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Input
                    label="Email address"
                    type="email"
                    value={form.email}
                    placeholder="pharmacy@example.com"
                    icon={Mail}
                    onChange={(value) => handleChange("email", value)}
                  />
                </div>

                <Input
                  label="Phone number"
                  value={form.phoneNumber}
                  placeholder="080..."
                  icon={Phone}
                  onChange={(value) => handleChange("phoneNumber", value)}
                />

                <Input
                  label="WhatsApp number"
                  value={form.whatsappNumber}
                  placeholder="080..."
                  icon={Phone}
                  onChange={(value) => handleChange("whatsappNumber", value)}
                />
              </div>
            </div>

            {/* Location */}
            <div>
              <div className="mb-4 flex items-center gap-2">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                  Location
                </p>

                <Tooltip text="Open Google Maps, right-click your pharmacy location, click 'What's here?', then copy latitude and longitude." />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Input
                    label="Address"
                    value={form.address}
                    placeholder="Full pharmacy address"
                    icon={MapPin}
                    onChange={(value) => handleChange("address", value)}
                  />
                </div>

                <Input
                  label="Latitude"
                  value={form.lat}
                  placeholder="4.8156"
                  icon={MapPin}
                  onChange={(value) => handleNumberChange("lat", value)}
                />

                <Input
                  label="Longitude"
                  value={form.lng}
                  placeholder="7.0498"
                  icon={MapPin}
                  onChange={(value) => handleNumberChange("lng", value)}
                />
              </div>
            </div>

            {/* Submit */}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className={`flex h-12 w-full items-center justify-center gap-2 rounded-xl text-sm font-semibold transition ${
                loading
                  ? "cursor-not-allowed bg-slate-200 text-slate-500"
                  : "bg-[#075E54] text-white hover:bg-[#064b43]"
              }`}
            >
              {loading ? (
                <>
                  <Loader2 size={17} className="animate-spin" />
                  Registering...
                </>
              ) : (
                "Register pharmacy"
              )}
            </button>
          </div>
        </section>

        <p className="mt-6 text-center text-xs leading-5 text-slate-400">
          Zaviramed pharmacy partners help patients access prescribed medicines
          safely and conveniently.
        </p>
      </div>
    </main>
  );
};

export default RegisterPharmacy;